<script setup lang="ts">
/**
 * Emoji picker as a panel to put into a popover or menu: search (German
 * words), category tabs, „recently used“ (kept in localStorage per browser)
 * and a grid walked with the arrow keys (Enter picks). `mode="quick"` is the
 * one-row reaction strip of a message menu, with an optional „more“ button.
 *
 * Emits `select` with the emoji; where to insert or react is the caller's.
 * The set is small and static (`NX_EMOJI_CATEGORIES`) — pass `categories` to
 * use another.
 */
import SmilePlus from 'lucide-vue-next/dist/esm/icons/smile-plus.js';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import {
  NX_EMOJI_CATEGORIES,
  NX_QUICK_REACTIONS,
  type NxEmoji,
  type NxEmojiCategory,
} from './emojiData';

const props = withDefaults(
  defineProps<{
    mode?: 'full' | 'quick';
    categories?: readonly NxEmojiCategory[];
    /** Emoji of the quick strip. */
    quick?: readonly string[];
    /** Quick strip only: show a „more“ button (emits `more`). */
    showMore?: boolean;
    /** localStorage key for recents; `null` switches recents off. */
    recentKey?: string | null;
    maxRecents?: number;
    columns?: number;
    searchLabel?: string;
    recentLabel?: string;
    emptyLabel?: string;
    moreLabel?: string;
    ariaLabel?: string;
  }>(),
  {
    mode: 'full',
    categories: () => NX_EMOJI_CATEGORIES,
    quick: () => NX_QUICK_REACTIONS,
    showMore: false,
    recentKey: 'nx-emoji-recent',
    maxRecents: 16,
    columns: 8,
    searchLabel: 'Emoji suchen',
    recentLabel: 'Zuletzt verwendet',
    emptyLabel: 'Kein Emoji gefunden',
    moreLabel: 'Weitere Reaktionen',
    ariaLabel: 'Emoji auswählen',
  }
);

const emit = defineEmits<{ select: [emoji: string]; more: [] }>();

// ── Recents (per browser; storage may be missing or refuse) ─────────────────
function readRecents(): string[] {
  if (!props.recentKey) return [];
  try {
    const raw = globalThis.localStorage?.getItem(props.recentKey);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((e): e is string => typeof e === 'string') : [];
  } catch {
    return [];
  }
}
const recents = ref<string[]>(readRecents());
function remember(emoji: string) {
  if (!props.recentKey) return;
  recents.value = [emoji, ...recents.value.filter((e) => e !== emoji)].slice(0, props.maxRecents);
  try {
    globalThis.localStorage?.setItem(props.recentKey, JSON.stringify(recents.value));
  } catch {
    /* storage full or blocked: recents stay for this session */
  }
}

function pick(emoji: string) {
  remember(emoji);
  emit('select', emoji);
}

// ── Search & sections ────────────────────────────────────────────────────────
const query = ref('');
const activeCategory = ref<string>('');

interface Section {
  id: string;
  label: string;
  emojis: readonly NxEmoji[];
}

const sections = computed<Section[]>(() => {
  const q = query.value.trim().toLowerCase();
  if (q) {
    const hits = props.categories.flatMap((c) =>
      c.emojis.filter((e) => e.k.includes(q) || c.label.toLowerCase().includes(q) || e.e === q)
    );
    return [{ id: 'search', label: '', emojis: hits }];
  }
  const all: Section[] = [];
  if (recents.value.length) {
    all.push({
      id: 'recent',
      label: props.recentLabel,
      emojis: recents.value.map((e) => ({ e, k: '' })),
    });
  }
  for (const c of props.categories) all.push({ id: c.id, label: c.label, emojis: c.emojis });
  return all;
});

/** All cells in reading order, for keyboard walking. */
const flat = computed(() => sections.value.flatMap((s) => s.emojis.map((e) => e.e)));
const focusIndex = ref(0);
const gridRef = useTemplateRef<HTMLElement>('grid');

function cellIndex(sectionIndex: number, i: number): number {
  let n = 0;
  for (let s = 0; s < sectionIndex; s++) n += sections.value[s]!.emojis.length;
  return n + i;
}

function focusCell(index: number) {
  const max = flat.value.length - 1;
  if (max < 0) return;
  focusIndex.value = Math.max(0, Math.min(max, index));
  void nextTick(() => {
    gridRef.value
      ?.querySelector<HTMLElement>(`[data-cell="${focusIndex.value}"]`)
      ?.focus({ preventScroll: false });
  });
}

function onGridKeydown(event: KeyboardEvent) {
  const i = focusIndex.value;
  const step: Record<string, number> = {
    ArrowRight: 1,
    ArrowLeft: -1,
    ArrowDown: props.columns,
    ArrowUp: -props.columns,
  };
  if (event.key in step) {
    event.preventDefault();
    focusCell(i + step[event.key]!);
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusCell(0);
  } else if (event.key === 'End') {
    event.preventDefault();
    focusCell(flat.value.length - 1);
  }
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'Enter') {
    event.preventDefault();
    if (event.key === 'Enter' && flat.value[0]) pick(flat.value[0]);
    else focusCell(0);
  }
}

function jumpTo(categoryId: string) {
  activeCategory.value = categoryId;
  query.value = '';
  void nextTick(() => {
    gridRef.value
      ?.querySelector<HTMLElement>(`[data-section="${categoryId}"]`)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });
}

// ── Quick strip ──────────────────────────────────────────────────────────────
function onQuickKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
  const buttons = Array.from(
    (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('button')
  );
  const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
  const next = (i + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
  event.preventDefault();
  buttons[next]?.focus();
}
</script>

<template>
  <div
    v-if="props.mode === 'quick'"
    class="nx-emoji-quick"
    role="group"
    :aria-label="props.ariaLabel"
    @keydown="onQuickKeydown"
  >
    <button
      v-for="emoji in props.quick"
      :key="emoji"
      type="button"
      class="nx-emoji-quick__btn"
      :aria-label="emoji"
      data-testid="nx-emoji-quick"
      @click="pick(emoji)"
    >
      {{ emoji }}
    </button>
    <button
      v-if="props.showMore"
      type="button"
      class="nx-emoji-quick__btn nx-emoji-quick__more"
      :aria-label="props.moreLabel"
      :title="props.moreLabel"
      @click="emit('more')"
    >
      <SmilePlus class="nxt-icon-sm" aria-hidden="true" />
    </button>
  </div>

  <div
    v-else
    class="nx-emoji-picker"
    role="group"
    :aria-label="props.ariaLabel"
    :style="{ '--emoji-cols': String(props.columns) }"
  >
    <input
      v-model="query"
      class="nx-emoji-picker__search"
      type="search"
      :placeholder="props.searchLabel"
      :aria-label="props.searchLabel"
      data-testid="nx-emoji-search"
      @keydown="onSearchKeydown"
    />
    <div v-if="!query" class="nx-emoji-picker__tabs" role="toolbar" :aria-label="props.ariaLabel">
      <button
        v-for="c in props.categories"
        :key="c.id"
        type="button"
        class="nx-emoji-picker__tab"
        :class="{ 'is-active': activeCategory === c.id }"
        :title="c.label"
        :aria-label="c.label"
        @click="jumpTo(c.id)"
      >
        {{ c.icon }}
      </button>
    </div>
    <div ref="grid" class="nx-emoji-picker__scroll" @keydown="onGridKeydown">
      <template v-for="(section, si) in sections" :key="section.id">
        <div
          v-if="section.label"
          class="nx-emoji-picker__section"
          :data-section="section.id"
          role="presentation"
        >
          {{ section.label }}
        </div>
        <div class="nx-emoji-picker__grid" role="group" :aria-label="section.label || undefined">
          <button
            v-for="(item, i) in section.emojis"
            :key="`${section.id}-${item.e}`"
            type="button"
            class="nx-emoji-picker__cell"
            :tabindex="cellIndex(si, i) === focusIndex ? 0 : -1"
            :data-cell="cellIndex(si, i)"
            :aria-label="item.k ? `${item.e} ${item.k.split(' ')[0]}` : item.e"
            data-testid="nx-emoji-cell"
            @click="pick(item.e)"
            @focus="focusIndex = cellIndex(si, i)"
          >
            {{ item.e }}
          </button>
        </div>
      </template>
      <p v-if="!flat.length" class="nx-emoji-picker__empty">{{ props.emptyLabel }}</p>
    </div>
  </div>
</template>

<style scoped>
.nx-emoji-quick {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
}
.nx-emoji-quick__btn {
  display: inline-grid;
  place-items: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  font-size: var(--nxt-font-size-lg);
  line-height: 1;
  cursor: pointer;
  transition:
    transform var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-emoji-quick__btn:hover,
.nx-emoji-quick__btn:focus-visible {
  background: var(--nxt-surface-raised-bg);
  transform: scale(1.18);
  outline: none;
}
.nx-emoji-quick__btn:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-emoji-quick__more {
  color: var(--nxt-fg-muted);
}

.nx-emoji-picker {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: calc(var(--emoji-cols) * var(--nxt-control-size-sm) + var(--nxt-space-sm) * 2);
  max-width: 100%;
  padding: var(--nxt-space-sm);
}
.nx-emoji-picker__search {
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
}
.nx-emoji-picker__search:focus-visible {
  outline: none;
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-emoji-picker__tabs {
  display: flex;
  gap: var(--nxt-space-2xs);
  padding-block-end: var(--nxt-space-2xs);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-emoji-picker__tab {
  flex: 1 1 0;
  height: var(--nxt-control-size-xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  font-size: var(--nxt-font-size-md);
  cursor: pointer;
  filter: grayscale(0.6);
  opacity: 0.75;
}
.nx-emoji-picker__tab:hover,
.nx-emoji-picker__tab.is-active,
.nx-emoji-picker__tab:focus-visible {
  background: var(--nxt-surface-sunken-bg);
  filter: none;
  opacity: 1;
  outline: none;
}
.nx-emoji-picker__scroll {
  max-height: calc(var(--nxt-control-size-sm) * 6.5);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.nx-emoji-picker__section {
  position: sticky;
  top: 0;
  z-index: var(--nxt-z-raised);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-emoji-picker__grid {
  display: grid;
  grid-template-columns: repeat(var(--emoji-cols), var(--nxt-control-size-sm));
}
.nx-emoji-picker__cell {
  display: grid;
  place-items: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  font-size: var(--nxt-font-size-lg);
  line-height: 1;
  cursor: pointer;
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-emoji-picker__cell:hover,
.nx-emoji-picker__cell:focus-visible {
  background: var(--nxt-menu-item-bg-hover, var(--nxt-surface-sunken-bg));
  transform: scale(1.12);
  outline: none;
}
.nx-emoji-picker__cell:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-emoji-picker__empty {
  margin: 0;
  padding: var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
@media (prefers-reduced-motion: reduce) {
  .nx-emoji-quick__btn,
  .nx-emoji-picker__cell {
    transition: none;
  }
  .nx-emoji-quick__btn:hover,
  .nx-emoji-quick__btn:focus-visible,
  .nx-emoji-picker__cell:hover,
  .nx-emoji-picker__cell:focus-visible {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-emoji-quick__btn,
:where([data-nx-motion='reduced']) .nx-emoji-picker__cell {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-emoji-quick__btn:hover,
:where([data-nx-motion='reduced']) .nx-emoji-quick__btn:focus-visible,
:where([data-nx-motion='reduced']) .nx-emoji-picker__cell:hover,
:where([data-nx-motion='reduced']) .nx-emoji-picker__cell:focus-visible {
  transform: none;
}
</style>
